/**
 * Types partagés avec le backend.
 *
 * Tous les nombres décimaux arrivent en **chaînes** : le backend calcule en
 * `Decimal` et refuse de les dégrader en flottants JSON. Le frontend les
 * affiche telles quelles et ne les convertit en nombre que pour les champs de
 * saisie.
 */

export type Side = 'BUY' | 'SELL'

export type ProtectionPolicy =
  | 'both'
  | 'stop_loss'
  | 'take_profit'
  | 'at_least_one'
  | 'optional'

/** Mode d'exécution global (§7). Persistant, visible en permanence. */
export type ExecutionMode = 'calculator' | 'confirmation' | 'auto'

export interface Instrument {
  name: string
  broker_name: string
  base_asset: string
  quote_asset: string
  category: 'major' | 'cross'
  digits: number
  pip_position: number
  pip_size: string
  lot_size: number
  min_lots: string
  max_lots: string
  step_lots: string
  enabled: boolean
}

export interface InstrumentsResponse {
  instruments: Instrument[]
  majors: string[]
  crosses: string[]
}

/** Un des deux blocs affichés côte à côte. */
export interface OrderView {
  label: string
  symbol: string
  side: Side
  lots: string
  stop_loss_pips: string | null
  take_profit_pips: string | null
  risk_reward: string | null
}

export interface VolumeView {
  lots: string
  requested_lots: string
  api_volume: number
  below_minimum: boolean
  above_maximum: boolean
  is_tradable: boolean
}

export interface PlanResponse {
  entry: OrderView
  sent: OrderView
  account_currency: string
  /** Capital sur lequel TOUS les chiffres de la réponse sont calculés. */
  account_balance: string
  /** `broker` : le compte réel. `manual` : le capital saisi (mode calculateur). */
  account_source: 'broker' | 'manual'
  pip_value_per_lot: string
  pip_value_total: string
  /** Perte RÉELLE si le stop envoyé est touché — le seul chiffre que les
   *  garde-fous regardent, quelle que soit la base de dimensionnement. */
  risk_amount: string | null
  /** Gain réel si la cible envoyée est touchée, c'est-à-dire le montant en jeu
   *  sur MON stop — celui sur lequel l'interface dimensionne par défaut. */
  reward_amount: string | null
  risk_percent_of_balance: string | null
  reward_percent_of_balance: string | null
  margin_required: string
  free_margin_after: string
  /** Écart bid/ask au moment du calcul, en pips. `null` sans cotation. */
  spread_pips: string | null
  /**
   * Ce que cet écart coûte à l'ouverture, pour ce volume.
   *
   * Le seul coût que l'inversion NE renverse PAS : le stop et la cible
   * permutent, le sens permute, le spread se paie du même montant.
   */
  spread_cost: string | null
  volume: VolumeView
  relative_stop_loss: number | null
  relative_take_profit: number | null
  conversion_paths: string[]
  /** Prix à saisir pour que le calcul aboutisse. Vide si tout est connu. */
  missing_quotes: string[]
  protection_policy: ProtectionPolicy
  protection_broker_side: string
  protection_entry_side: string
  warnings: string[]
  errors: string[]
  is_sendable: boolean
}

export interface QuoteIn {
  symbol: string
  price?: string
  bid?: string
  ask?: string
}

export interface AccountIn {
  currency: string
  balance: string
  leverage: number
}

export interface PlanRequest {
  symbol: string
  side: Side
  stop_loss_pips?: string | null
  take_profit_pips?: string | null
  /** Exactement un des trois doit être fourni (dimensionnement bidirectionnel). */
  lots?: string
  risk_amount?: string
  risk_percent?: string
  quotes: QuoteIn[]
  account: AccountIn
  protection_policy?: ProtectionPolicy
  /**
   * Sur quel stop porte le montant risqué.
   *
   * `platform` : mon stop, le raisonnement habituel du trader — c'est le
   * défaut. `broker` : le stop réellement envoyé, où le montant saisi est
   * exactement la perte possible.
   */
  sizing_basis?: SizingBasis
}

export type SizingBasis = 'platform' | 'broker'

/** Quelle valeur pilote le calcul. Les deux autres sont recalculées (§5). */
export type SizingMode = 'lots' | 'amount' | 'percent'

/** Une bougie, telle que la renvoie le backend et que l'attend le graphique. */
export interface Candle {
  /** Horodatage Unix en secondes. */
  time: number
  open: string
  high: string
  low: string
  close: string
  volume: number
}

export type Timeframe = 'M1' | 'M5' | 'M15' | 'M30' | 'H1' | 'H4' | 'D1'

export const TIMEFRAMES: Timeframe[] = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1']

/**
 * Durée d'une bougie, en minutes.
 *
 * Une seule table pour toute l'application : ajouter M30 a demandé de
 * retoucher sept tables dispersées, et une table oubliée donne un intervalle
 * qui s'affiche mais se calcule faux.
 */
export const TIMEFRAME_MINUTES: Record<Timeframe, number> = {
  M1: 1,
  M5: 5,
  M15: 15,
  M30: 30,
  H1: 60,
  H4: 240,
  D1: 1440,
}

/**
 * Nombre de bougies chargées, par intervalle.
 *
 * L'objectif est de couvrir **au moins 18 mois** là où cela a un sens :
 * 550 bougies journalières (≈ 390 jours ouvrés en 18 mois) et 2 400 bougies de
 * 4 heures (6 par jour ouvré) y suffisent.
 *
 * Sur les intervalles courts, 18 mois est hors d'atteinte et sans intérêt :
 * il faudrait 780 000 bougies d'une minute, que ni le broker ni le navigateur
 * ne suivraient. On charge alors la plus longue fenêtre exploitable.
 */
export const CANDLE_COUNT: Record<Timeframe, number> = {
  M1: 1500, // ≈ 2 jours de séance
  M5: 2000, // ≈ 1 semaine
  M15: 2500, // ≈ 1 mois
  M30: 2500, // ≈ 2 mois
  H1: 2500, // ≈ 5 mois
  H4: 2400, // ≈ 18 mois
  D1: 550, // ≈ 26 mois
}

export interface BrokerAccount {
  currency: string
  balance: string
  equity: string
  free_margin: string
  leverage: number
}

export interface BrokerStatus {
  configured: boolean
  authorized: boolean
  state: 'disconnected' | 'connecting' | 'connected' | 'authenticated'
  ready: boolean
  environment: 'demo' | 'live'
  endpoint: string
  account_id: number | null
  symbols_loaded: number
  quotes_received: number
  account: BrokerAccount | null
  error: string | null
}

/** Cotation poussée par le flux WebSocket. */
export interface LiveQuote {
  symbol: string
  bid: string
  ask: string
  mid: string
  spread: string
}

/** Messages du flux `/api/ws/market`. */
export type MarketMessage =
  | { type: 'quotes'; quotes: LiveQuote[] }
  | {
      type: 'status'
      ready: boolean
      state: BrokerStatus['state']
      error: string | null
      account: BrokerAccount | null
      /**
       * Secondes depuis la dernière cotation reçue par le serveur, toutes
       * paires confondues, par paliers de cinq. `null` avant la première.
       *
       * Le seul moyen de distinguer un flux mort d'un marché calme : un flux
       * coupé ne produit aucune erreur, le compte continue de répondre.
       */
      quote_age: number | null
    }

/**
 * Une position ouverte, vue des deux côtés.
 *
 * ⚠️ `broker_side` / `broker_profit` sont la réalité : la position ouverte et
 * l'argent réellement gagné ou perdu sur le compte.
 *
 * `entry_side` / `entry_profit` reconstituent la saisie d'origine. C'est un
 * indicateur de suivi de stratégie, **pas un solde** : gagner 100 en vue
 * logique, c'est perdre 100 sur le compte.
 */
export interface Position {
  position_id: number
  symbol: string
  broker_side: Side
  broker_profit: string | null
  entry_side: Side
  entry_profit: string | null
  lots: string
  entry_price: string
  current_price: string | null
  stop_loss: string | null
  take_profit: string | null
  swap: string
  commission: string
  used_margin: string
  open_timestamp: number

  /** Taille d'un pip pour cette paire. */
  pip_size: string
  /**
   * Valeur d'un pip pour CE volume, en devise du compte.
   *
   * `null` quand la cotation de conversion manque : l'écran affiche alors
   * « — » plutôt qu'un montant inventé.
   */
  pip_value: string | null
  account_currency: string
  account_balance: string | null
  /** Limite de risque par position, en pourcentage du capital. */
  max_risk_percent: string
  max_risk_amount: string | null
  /** Perte si le stop actuel est touché. */
  risk_amount: string | null
  /** Gain si la cible actuelle est touchée. */
  reward_amount: string | null
}

export interface OrderResponse {
  sent: boolean
  deal_reference: string | null
  client_order_id: string
  plan: PlanResponse
  warnings: string[]
  message: string
}

/** État de la surveillance automatique du capital. */
export interface GuardianStatus {
  enabled: boolean
  /** Vrai si le kill switch est armé : plus aucun ordre ne part. */
  tripped: boolean
  reason: string | null
  detail: string | null
  swing_limit_pct: string
  daily_loss_limit_pct: string
}

/* --------------------------------------------------------------------------
   Statistiques (onglet Stats)

   Rappel de lecture : tout ce qui commence par `strategy_` est exprimé dans
   la vue de la plateforme, c'est-à-dire l'INVERSE du compte. Tout ce qui
   commence par `real_` parle du compte réel. Les deux ne s'additionnent
   jamais.
-------------------------------------------------------------------------- */

export interface SymbolExposure {
  symbol: string
  positions: number
  lots: string
  strategy_pnl: string | null
}

export interface OpenStats {
  count: number
  lots: string
  strategy_pnl: string | null
  real_pnl: string | null
  real_margin: string
  /** Gain si TOUTES mes cibles étaient atteintes. */
  max_gain: string | null
  max_gain_percent: string | null
  /** Perte si TOUS mes stops étaient touchés. */
  max_loss: string | null
  max_loss_percent: string | null
  /** Positions sans cible : le gain n'a pas de plafond. */
  without_target: number
  /** Positions sans stop : la perte n'a pas de plafond. */
  without_stop: number
  by_symbol: SymbolExposure[]
}

export interface CurvePoint {
  at: number
  cumulative: string
}

export interface ClosedTradeRow {
  at: number
  symbol: string
  /** Résultat POUR MOI : l'opposé de celui du compte. */
  strategy_result: string
  /** Sens de MA saisie, retrouvé via le `dealId`. `null` si hors fenêtre. */
  entry_side: Side | null
  lots: string | null
  open_price: string | null
  currency: string
}

export interface ClosedStats {
  count: number
  wins: number
  losses: number
  flat: number
  win_rate: string | null
  strategy_pnl: string
  real_pnl: string
  average_win: string | null
  average_loss: string | null
  profit_factor: string | null
  best: string | null
  worst: string | null
  max_drawdown: string | null
  curve: CurvePoint[]
  /**
   * Frais de financement de la période.
   *
   * Ni inversés, ni ajoutés au résultat : un swap est un coût prélevé sur le
   * compte réel, il ne devient pas un gain parce qu'on lit la position à
   * l'envers.
   */
  financing: string
  financing_count: number
  trades: ClosedTradeRow[]
}

export interface ActivityEntry {
  at: number
  symbol: string
  /** open | close | amend | order | swap | system | other */
  kind: string
  raw_type: string
  status: string
  /** Sens de MA saisie. Le sens du courtier n'est pas renvoyé : cet onglet
   *  montre la plateforme. */
  entry_side: Side | null
  lots: string | null
  level: string | null
  stop_loss: string | null
  take_profit: string | null
}

export interface StatsResponse {
  currency: string
  balance: string | null
  days: number
  open: OpenStats
  closed: ClosedStats
  history_supported: boolean
  activity_supported: boolean
  activity: ActivityEntry[]
}

/* --------------------------------------------------------------------------
   Administration
-------------------------------------------------------------------------- */

export type UserRole = 'admin' | 'trader'

/**
 * Fournisseur technique d'un compte. Ce n'est PAS le courtier : FTMO et les
 * autres prop firms passent toutes par cTrader. Le nom commercial est dans
 * `broker_name`.
 */
export type AccountProvider = 'capital' | 'ctrader'

/** Un compte de trading, vu de l'administration ou d'un utilisateur. */
export interface AccountSummary {
  id: number
  label: string
  /** Nom à afficher : le nom choisi, ou « courtier · numéro » à défaut. */
  display_name: string
  provider: AccountProvider
  /** Courtier réel : « FTMO », « Capital.com »… */
  broker_name: string
  external_id: string
  /** Vrai si le compte engage de l'argent réel. */
  is_live: boolean
}

export interface ManagedUser {
  id: number
  username: string
  role: UserRole
  /** Identifiant chez le courtier du compte ACTIF, `null` s'il n'y en a pas. */
  broker_account_id: string | null
  active_account_id: number | null
  /** Comptes explicitement ouverts à cet utilisateur. */
  accounts: AccountSummary[]
  is_active: boolean
  created_at: string
  last_login_at: string | null
}

/** Un compte de l'utilisateur connecté, avec l'état de sa liaison. */
export interface MyAccount extends Omit<AccountSummary, 'label'> {
  /** Prix et ordres disponibles. */
  connected: boolean
  /** Ouverture en cours, quelques secondes. */
  starting: boolean
  error: string | null
}

export interface AccountsState {
  accounts: MyAccount[]
  /** Compte sur lequel tout se passe : écran ET ordres. */
  active_id: number | null
}

/** Un compte de trading, vu de l'administration. */
export interface TradingAccountAdmin extends AccountSummary {
  connection_id: number | null
  /** Qui l'a en accès explicite. */
  usernames: string[]
  connected: boolean
  error: string | null
}

export interface CTraderConnection {
  id: number
  label: string
  account_ids: number[]
  created_at: string
}

export interface CTraderStatus {
  /** Client ID et Client Secret enregistrés. */
  app_configured: boolean
  /** Début du Client ID, pour reconnaître l'application sans l'afficher. */
  client_id_hint: string
  /** À déclarer, à l'identique, dans l'application chez Spotware. */
  redirect_uri: string
  connections: CTraderConnection[]
}

/** Un compte disponible sous la clé API, proposé au choix. */
export interface BrokerAccountOption {
  broker_account_id: string
  name: string
  currency: string
  balance: string | null
  is_live: boolean
}

export interface CreateUserBody {
  username: string
  password: string
  role: UserRole
  broker_account_id?: string | null
  /** Comptes de trading existants à ouvrir à l'utilisateur. */
  account_ids?: number[]
}

/**
 * Modification partielle.
 *
 * Un champ absent signifie « ne touche pas ». Pour détacher un compte de
 * trading on envoie une chaîne VIDE, pas `null` — sans quoi « effacer » et
 * « ignorer » seraient impossibles à distinguer.
 */
export interface UpdateUserBody {
  password?: string
  role?: UserRole
  broker_account_id?: string
  /** Remplace la liste des comptes ouverts. Liste vide : plus aucun. */
  account_ids?: number[]
  is_active?: boolean
}
